import {
  collection,
  doc,
  getDocs,
  query,
  type QueryDocumentSnapshot,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import type { DynamicPage } from '@superfunnel/types/dynamic-page';
import type { DynamicPageVariant } from '@superfunnel/types/dynamic-page-variant';
import type { Experiment } from '@superfunnel/types/experiment';
import type { GlobalSection } from '@superfunnel/types/global-section';
import type { Website } from '@superfunnel/types/website';

import { db, functions } from '../Firebase';
import { emptyConverter } from '../utils';

export const globalSectionsColRef = (accountId: string) => {
  return collection(db, 'accounts', accountId, 'globalSections').withConverter(
    emptyConverter<GlobalSection>(),
  );
};

export const globalSectionsQuery = (accountId: string) => {
  return query(
    globalSectionsColRef(accountId),
    where('status', '!=', 'deleted'),
  );
};

export const publishGlobalSection = async (
  accountId: string,
  globalSectionId: string,
): Promise<any> => {
  const fn = httpsCallable(
    functions,
    'accounts/globalSections/publishGlobalSection',
  );

  const res = await fn({
    accountId,
    globalSectionId,
  });

  return res.data;
};

export interface GlobalSectionUsage {
  pageId: string;
  name: string;
  status: DynamicPage['status'];
  variantName: string;
  experimentName?: string;
  variationName?: string;
  websiteId: string;
}

interface VariantUsage {
  variant: DynamicPageVariant;
  websiteId: string;
}

const websiteUsesGlobalSection = (
  website: Website | undefined,
  globalSectionId: string,
) =>
  [
    ...(website?.development.sections ?? []),
    ...(website?.development.banners ?? []),
  ].some((section) => section.global?.globalId === globalSectionId);

const getActiveWebsites = (
  websiteDocs: QueryDocumentSnapshot<Website, Website>[],
) =>
  new Map(
    websiteDocs
      .filter((websiteDoc) => websiteDoc.data().status !== 'deleted')
      .map((websiteDoc) => [websiteDoc.id, websiteDoc.data()]),
  );

const getVariantUsages = (
  variantDocs: QueryDocumentSnapshot<DynamicPageVariant, DynamicPageVariant>[],
  websites: Map<string, Website>,
  globalSectionId: string,
): VariantUsage[] =>
  variantDocs.flatMap((variantDoc) => {
    const variant = variantDoc.data();
    if (variant.status === 'deleted') return [];

    return variant.websiteIds.flatMap((websiteId) =>
      websiteUsesGlobalSection(websites.get(websiteId), globalSectionId)
        ? [{ variant, websiteId }]
        : [],
    );
  });

const getVariationName = (
  experiment: Experiment | undefined,
  websiteId: string,
) => {
  if (experiment?.originalWebsiteId === websiteId) return 'Control';
  return experiment?.tests.find((test) => test.websiteId === websiteId)
    ?.description;
};

const getPageGlobalSectionUsages = async (
  pageDoc: QueryDocumentSnapshot<DynamicPage, DynamicPage>,
  globalSectionId: string,
): Promise<GlobalSectionUsage[]> => {
  const [websitesSnapshot, variantsSnapshot] = await Promise.all([
    getDocs(
      collection(pageDoc.ref, 'websites').withConverter(
        emptyConverter<Website>(),
      ),
    ),
    getDocs(
      collection(pageDoc.ref, 'variants').withConverter(
        emptyConverter<DynamicPageVariant>(),
      ),
    ),
  ]);
  const websites = getActiveWebsites(websitesSnapshot.docs);
  const variantUsages = getVariantUsages(
    variantsSnapshot.docs,
    websites,
    globalSectionId,
  );

  if (!variantUsages.length) return [];

  const experimentsSnapshot = await getDocs(
    collection(pageDoc.ref, 'experiments').withConverter(
      emptyConverter<Experiment>(),
    ),
  );
  const experiments = new Map(
    experimentsSnapshot.docs.map((experimentDoc) => [
      experimentDoc.id,
      experimentDoc.data(),
    ]),
  );
  const page = pageDoc.data();

  return variantUsages.map(({ variant, websiteId }) => {
    const experiment = variant.currentExperimentId
      ? experiments.get(variant.currentExperimentId)
      : undefined;

    return {
      pageId: pageDoc.id,
      name: page.name,
      status: page.status,
      variantName: variant.name,
      experimentName: experiment?.name,
      variationName: getVariationName(experiment, websiteId),
      websiteId,
    };
  });
};

export const deleteGlobalSection = async (
  accountId: string,
  globalSectionId: string,
): Promise<void> => {
  await updateDoc(doc(globalSectionsColRef(accountId), globalSectionId), {
    status: 'deleted',
  });
};

export const getGlobalSectionUsages = async (
  dynamicPages: QueryDocumentSnapshot<DynamicPage, DynamicPage>[],
  globalSectionId: string,
): Promise<GlobalSectionUsage[]> => {
  const pageUsages = await Promise.all(
    dynamicPages.map((pageDoc) =>
      getPageGlobalSectionUsages(pageDoc, globalSectionId),
    ),
  );

  return pageUsages.flat();
};
